import { createStore } from 'vuex';
import * as api from '../services/api';
import { read as readPreferences, write as writePreferences, applyMotion } from '../lib/preferences';

/**
 * Estado do jogo.
 *
 * O servidor é dono da partida: aqui só mora o que ele revelou, mais o que o jogador está
 * digitando agora. Nenhuma regra é recalculada — pontuação, avanço e fim de partida vêm
 * prontos da resposta. Ver docs/AI_DIRETIVES/COMPONENTS.md § 5.
 */

/**
 * Última partida deste navegador. Só o id: o estado é todo do servidor.
 *
 * Serve para dois retornos diferentes — retomar a partida em andamento e reabrir o
 * resultado da que fechou —, e quem decide qual dos dois é o `init`, perguntando ao
 * servidor em que estado ela está.
 */
const GAME_KEY = 'sentido:game';

/**
 * Modo em que o jogador está — inclusive a trilha.
 *
 * `mode` guarda a chave da **partida**, e a trilha não tem chave escolhível: sem este
 * registro, jogar uma fase deixava `mode` no último diário, e o app não sabia para onde
 * "voltar ao modo" depois que a fase acabava. A trilha tem duas telas, mapa e partida, e é
 * o mapa que a representa quando não há partida aberta.
 */
const MODE_KEY = 'sentido:mode';

export const XP_POR_NIVEL = 500;

/** Em que modo essa partida foi jogada — a trilha e o duelo não têm chave na lista. */
function modeOf(game) {
  if (game.trail_phase) return 'trail';
  if (game.duel_id) return 'duel';
  return game.mode;
}

/** O plano aponta para a partida que já está aberta? */
function isSameGame(game, plan) {
  if (plan.kind === 'phase') return game.trail_phase === plan.value;
  if (plan.kind === 'duel') return game.duel_id === plan.value;
  return !!plan.value && game.mode === plan.value;
}

/** O id guardado, ou `null` quando o navegador não tem armazenamento. */
function readStoredGame() {
  try {
    return localStorage.getItem(GAME_KEY);
  } catch {
    return null;
  }
}

function readStoredMode() {
  try {
    return localStorage.getItem(MODE_KEY) || null;
  } catch {
    return null;
  }
}

function forgetStoredGame() {
  try {
    localStorage.removeItem(GAME_KEY);
  } catch {
    // sem armazenamento, nada a limpar
  }
}

/** O que `GET /api/me` devolve antes de a primeira resposta chegar. */
function emptyProfile() {
  return {
    is_anonymous: true,
    display_name: null,
    must_change_password: false,
    level: 1,
    xp: 0,
    xp_into_level: 0,
    title: 'Primeiro fio',
    stats: {
      games_played: 0, wins: 0, losses: 0, total_points: 0, best_score: 0,
      current_streak: 0, max_streak: 0, clean_words: 0, perfect_chains: 0, average_points: 0,
    },
    achievements: [],
    medals: [],
  };
}

/** Remove acento e caixa, como o servidor faz antes de comparar. */
function fold(text) {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase();
}

/** Quantas casas do trecho palpitável já têm letra. Com buracos, `length` mente. */
function countTyped(typed, remaining) {
  let total = 0;
  for (let i = 0; i < remaining; i += 1) if (typed[i]) total += 1;
  return total;
}

/**
 * Primeira casa vazia a partir de `from`, dando a volta pelo começo.
 *
 * Depois de escrever, o cursor procura buraco em vez de andar uma casa: preencher o meio
 * de uma palavra quase pronta e cair em cima de uma letra já digitada é o jeito rápido de
 * apagar o que estava certo. Se não sobrou buraco, ele para na casa seguinte — a palavra
 * está completa e o próximo passo é enviar.
 */
function nextGap(typed, remaining, from) {
  for (let i = from; i < remaining; i += 1) if (!typed[i]) return i;
  for (let i = 0; i < from && i < remaining; i += 1) if (!typed[i]) return i;
  return Math.min(from, remaining - 1);
}

/** Quantas casas a palavra corrente ainda pede, ou `null` sem partida aberta. */
function remainingOf(state) {
  const word = state.game?.words.find((w) => w.position === state.game.current_position);
  if (!word) return null;
  return word.length - (word.revealed || '').length;
}

export default createStore({
  state: {
    deviceId: null,
    /** Preferências do aparelho — teclado, pontos por palavra, movimento. */
    preferences: readPreferences(),
    modes: [],
    mode: 'classic-daily',
    /** `trail`, `duel` ou a chave de um modo de partida. Sobrevive ao fim da partida. */
    currentModeKey: readStoredMode(),
    /**
     * **Só partida em andamento.** Um slot que ora guarda partida ativa, ora guarda
     * partida terminada é o que fazia "ver resultado" de outro modo apagar a partida
     * aberta em silêncio: o mesmo campo significava duas coisas.
     */
    game: null,
    /**
     * Partida terminada em **leitura**: `{ game, summary }`. Ela nunca é o estado base da
     * tela de jogo — é sempre uma folha por cima do que estiver embaixo.
     */
    result: null,
    summary: null,
    /**
     * Troca pedida que encerraria a partida aberta, esperando confirmação. É dado puro
     * (`{ tipo, valor }`) porque o diálogo vive no casco do app: qualquer porta de entrada
     * — cartão de Hoje, folha de modos, nó da trilha, lista de duelos — passa por aqui.
     */
    /**
     * O `init` já respondeu. A tela de jogo espera por isto: decidir o estado base antes
     * da reidratação abria a folha de modos por cima de uma partida que estava voltando.
     */
    ready: false,
    /**
     * Há um fecho esperando para ser mostrado — pedido pelo jogador ("ver resultado") ou
     * reidratado por um F5 na tela de jogo. Ele abre **uma vez**: navegar entre abas depois
     * disso cai na folha de modos, com o fecho a um toque dentro dela.
     */
    resultToShow: false,
    /**
     * Retorno por casa do último palpite e o melhor resultado por letra. Vêm do servidor —
     * a avaliação é regra de jogo, e regra de jogo não se recalcula no cliente.
     */
    lastGuess: [],
    keyboard: {},
    pendingSwitch: null,
    /** Rota pedida enquanto um duelo corre. Sair do duelo é desistir dele. */
    pendingExit: null,
    /**
     * O que o jogador digitou, **por casa** do trecho palpitável — e não uma pilha na
     * ordem da digitação. Com o seletor de casa a ordem deixou de ser a da fila: dá para
     * preencher a terceira antes da segunda, e o buraco fica no índice dele.
     */
    typed: [],
    /** Casa que recebe a próxima letra, contada dentro do trecho palpitável. */
    caret: 0,
    message: null,
    me: emptyProfile(),
    loading: false,
    locked: false,
    lastOutcome: null,
    // Modo trilha: o mapa que o servidor devolveu e o fecho da última fase jogada.
    trail: null,
    trailOutcome: null,
    // Duelo: a lista, o fecho da minha partida e o relógio que o servidor mandou.
    duels: [],
    duelOutcome: null,
    secondsLeft: null,
    duelLimit: null,
    // Segundos de largada: enquanto eles correm, a tela mostra a contagem e o tabuleiro
    // fica escondido. O relógio da partida só começa depois.
    countdown: 0,
  },

  getters: {
    /**
     * **A partida ativa, e nada mais.** O estado base da rota "/" sai só daqui: enquanto
     * "o jogo em foco" também podia ser um resultado terminado, a base passava a desenhar
     * o resultado e a folha de modos vinha empilhada por cima.
     */
    activeGame: (state) => state.game,
    preferences: (state) => state.preferences,
    currentModeKey: (state) => state.currentModeKey,
    /**
     * Há partida **em andamento**? O slot deveria guardar só isso, e o `finish` o esvazia
     * ao fechar a chain — mas quem responde por "existe partida" é este getter, e ele não
     * pode depender de todo caminho de fim ter passado por lá. Partida encerrada que
     * sobrasse no slot ligava o aviso de desistência em cima de uma chain já respondida.
     */
    hasGame: (state) => state.game !== null && state.game.state === 'in_progress',
    /** Fase que a partida ativa está resolvendo. É por ela que a trilha reconhece a vez. */
    activePhase: (state) => state.game?.trail_phase ?? null,
    /** O que o overlay de resultado mostra. Dado de folha: a base nunca depende disto. */
    result: (state) => state.result,
    resultOpen: (state) => state.resultToShow && !!state.result,
    /**
     * Retorno por casa, indexado pela posição na palavra. Casa revelada pela regra mostra a
     * própria letra: ela é conhecida, e a banda continua contínua na linha.
     */
    /**
     * O retorno do último palpite, indexado pela posição na palavra.
     *
     * **A tela não decide nada aqui.** O servidor manda a palavra inteira já resolvida — a
     * casa que a regra deu vem como `given`, as que o jogador digitou vêm avaliadas — e
     * este getter só troca a lista por um mapa, que é o que o tabuleiro sabe ler.
     *
     * Antes o cliente montava as casas dadas e ainda rebaixava para "já conhecida" toda
     * casa âmbar cuja letra aparecesse no prefixo. Parecia certo e apagava descoberta: em
     * ARAME, com o A dado, o A digitado na última casa aponta o **segundo** A, que ainda
     * está escondido. Quem conhece a palavra é o servidor; a decisão é dele.
     */
    dockByPosition: (state) => {
      const map = {};
      state.lastGuess.forEach((slot) => { map[slot.position] = slot; });
      return map;
    },
    keyboard: (state) => state.keyboard,
    /** Fecho a mostrar: o do resultado em leitura, ou o da partida que acabou agora. */
    summary: (state) => state.result?.summary || state.summary || null,
    isOver: (state) => !!state.game && state.game.state !== 'in_progress',
    isScored: (state) => !!state.game && state.game.scored,
    score: (state) => (state.game ? state.game.score : 0),

    currentWord: (state) => {
      if (!state.game) return null;
      return state.game.words.find((w) => w.position === state.game.current_position) || null;
    },

    revealedCount: (state, getters) => (getters.currentWord?.revealed || '').length,
    worth: (state, getters) => getters.currentWord?.worth ?? 0,

    /** Uma casa por letra da palavra corrente, com o estado que a interface desenha. */
    tiles: (state, getters) => {
      const word = getters.currentWord;
      if (!word || getters.isOver) return [];
      const revealed = word.revealed || '';
      const cursor = state.caret + revealed.length;

      return Array.from({ length: word.length }, (_, i) => {
        if (i < revealed.length) return { char: revealed[i], state: 'given', selected: false };
        const typedChar = state.typed[i - revealed.length];
        // A casa escolhida é marcada mesmo cheia: com o seletor, o cursor pode parar em
        // cima de uma letra, e sem marca ninguém sabe qual delas a próxima tecla troca.
        if (typedChar) return { char: typedChar, state: 'typed', selected: i === cursor };
        return { char: '', state: i === cursor ? 'cursor' : 'empty', selected: i === cursor };
      });
    },

    canSubmit: (state, getters) => {
      const word = getters.currentWord;
      // Sem partida aberta não há palpite: o tabuleiro na tela pode ser só leitura.
      if (!state.game || !word || state.locked || getters.isOver) return false;
      const remaining = word.length - (word.revealed || '').length;
      return countTyped(state.typed, remaining) === remaining;
    },

    // A trilha inteira vem do servidor: capítulo, tipo de fase e liberação já vêm
    // decididos, e o cliente só desenha — destravar aqui não abriria nada.
    trail: (state) => state.trail,
    trailPhases: (state) => state.trail?.phases || [],
    trailRules: (state) => state.trail?.rules || null,
    trailLives: (state) => state.trail?.lives ?? 0,
    trailOutcome: (state) => state.trailOutcome,
    /** Fase que a partida ativa está resolvendo, ou `null` fora da trilha. */
    gamePhase: (state) => state.game?.trail_phase ?? null,
    trailChapters: (state) => {
      const fases = state.trail?.phases || [];
      const capitulos = [];
      fases.forEach((phase) => {
        const current = capitulos[capitulos.length - 1];
        if (current && current.number === phase.chapter) current.phases.push(phase);
        else capitulos.push({ number: phase.chapter, phases: [phase] });
      });
      return capitulos;
    },
    duels: (state) => state.duels,
    duelOutcome: (state) => state.duelOutcome,
    /** Duelo da partida ativa, ou `null` fora do duelo. */
    gameDuel: (state) => state.game?.duel_id || null,
    /** Duelo **em andamento**: é ele que faz sair da tela valer desistência. */
    activeDuel: (state) => state.game?.duel_id || null,
    // O relógio vem do servidor a cada resposta; aqui ele só é guardado para a tela
    // descontar entre uma jogada e outra.
    secondsLeft: (state) => state.secondsLeft,
    duelLimit: (state) => state.duelLimit || 90,
    countdown: (state) => state.countdown,
    modes: (state) => state.modes,
    currentMode: (state) => state.mode,
    dailyMode: (state) => state.modes.find((m) => m.is_daily) || null,
    // O treino é o primeiro modo não diário que o servidor ofereceu. Quando não há
    // nenhum, quem chama esconde o botão em vez de mandar o jogador para um 503.
    trainingMode: (state) => state.modes.find((m) => !m.is_daily) || null,
    // Progressão é calculada no servidor: o cliente exibe, não deriva.
    stats: (state) => state.me.stats,
    level: (state) => state.me.level,
    xpIntoLevel: (state) => state.me.xp_into_level,
    levelTitle: (state) => state.me.title,
    isAnonymous: (state) => state.me.is_anonymous,
    displayName: (state) => state.me.display_name,
    // Senha redefinida pelo painel: nenhuma tela abre antes da troca.
    mustChangePassword: (state) => state.me.must_change_password === true,
    achievements: (state) => state.me.achievements,
    // Medalhas de temporada: o ranking zera na virada, elas não (Frente 8).
    medals: (state) => state.me.medals || [],
    // Contadores sociais do próprio jogador, para o resumo que abre a folha de amigos.
    social: (state) => ({
      friends: state.me.friends || 0,
      requests: state.me.friend_requests || 0,
      followers: state.me.followers || 0,
      following: state.me.following || 0,
    }),
  },

  mutations: {
    /** Grava a preferência trocada e reflete no aparelho o que é de folha de estilo. */
    setPreference(state, { key, value }) {
      state.preferences = writePreferences({ ...state.preferences, [key]: value });
      if (key === 'reducedMotion') applyMotion(value);
    },

    setDeviceId(state, id) {
      state.deviceId = id;
    },
    setModes(state, modes) {
      state.modes = modes;
    },
    setMode(state, mode) {
      state.mode = mode;
    },
    /** O modo em que o jogador está. Só a folha de seleção o troca. */
    setCurrentModeKey(state, mode) {
      state.currentModeKey = mode;
      try {
        if (mode) localStorage.setItem(MODE_KEY, mode);
        else localStorage.removeItem(MODE_KEY);
      } catch {
        // sem armazenamento, o modo vale só nesta sessão
      }
    },
    setGame(state, game) {
      // Guardar o id é o que faz um F5 voltar para onde o jogador estava: na partida, se
      // ela seguia aberta, ou no resultado dela, se fechou. A API não tem "minha partida",
      // ela responde por id.
      try {
        if (game?.game_id) localStorage.setItem(GAME_KEY, game.game_id);
      } catch {
        // modo privado: perde-se a retomada, não a partida.
      }
      // Partida nova apaga o resultado que estava aberto: a folha de fim é da anterior.
      state.result = null;
      if (game) state.resultToShow = false;
      // O retorno é da palavra corrente daquela partida; partida nova começa sem ele.
      state.lastGuess = game?.words?.find((w) => w.position === game.current_position)?.last_guess || [];
      state.keyboard = game?.keyboard || {};
      state.game = game;
      state.typed = [];
      state.caret = 0;
      state.summary = null;
      state.lastOutcome = null;
      state.locked = false;
      state.secondsLeft = null;
      state.duelLimit = null;
      state.countdown = 0;

      // O fecho da fase e o do duelo pertencem à partida **anterior**, e a folha de fim os
      // lê depois que ela acaba — por isso eles só somem quando entra uma partida nova, e
      // não quando a que terminou sai do slot. Sem a distinção, um treino jogado depois de
      // uma fase herdava as estrelas e o botão "voltar à trilha" dela.
      if (game) {
        state.trailOutcome = null;
        state.duelOutcome = null;
      }
    },
    /** Resultado em leitura. Nunca toca em `game`: quem joga continua jogando. */
    setResult(state, result) {
      state.result = result;
    },
    setReady(state, value) {
      state.ready = value;
    },
    setResultToShow(state, value) {
      state.resultToShow = value;
    },
    setFeedback(state, { letters, keyboard }) {
      state.lastGuess = letters || [];
      state.keyboard = keyboard || {};
    },
    setPendingSwitch(state, plan) {
      state.pendingSwitch = plan;
    },
    setPendingExit(state, rota) {
      state.pendingExit = rota;
    },
    setSummary(state, summary) {
      state.summary = summary;
    },
    setLoading(state, value) {
      state.loading = value;
    },
    setLocked(state, value) {
      state.locked = value;
    },
    setMessage(state, message) {
      state.message = message;
    },
    setOutcome(state, outcome) {
      state.lastOutcome = outcome;
    },
    typeLetter(state, letter) {
      const remaining = remainingOf(state);
      if (remaining === null || state.caret >= remaining) return;
      // Escrever por índice, e não empilhar: a casa escolhida é a que recebe a letra, e a
      // que já tinha uma é sobrescrita — quem tocou nela queria trocá-la.
      const typed = [...state.typed];
      typed[state.caret] = letter;
      state.typed = typed;
      state.caret = nextGap(typed, remaining, state.caret + 1);
    },
    /**
     * Apagar segue o cursor, como em campo de código: casa preenchida some e o cursor
     * fica; casa vazia manda o cursor um passo atrás e apaga o que houver lá.
     */
    deleteLetter(state) {
      const remaining = remainingOf(state);
      if (remaining === null) return;
      const typed = [...state.typed];
      let index = state.caret;
      if (!typed[index]) index = Math.max(0, index - 1);
      typed[index] = undefined;
      state.typed = typed;
      state.caret = index;
    },
    /** Escolha direta de casa. Fora do trecho palpitável não existe casa para escolher. */
    setCaret(state, index) {
      const remaining = remainingOf(state);
      if (remaining === null || index < 0 || index >= remaining) return;
      state.caret = index;
    },
    clearTyped(state) {
      state.typed = [];
      state.caret = 0;
    },
    /** Reflete no estado local o que o servidor respondeu ao palpite. */
    applyGuess(state, result) {
      const game = state.game;
      game.score = result.score;
      game.state = result.state;
      game.current_position = result.current_position;

      const word = game.words.find((w) => w.position === state.lastPosition);
      if (word && result.word) word.word = result.word;

      game.words = game.words.map((w) => {
        if (w.position === result.current_position && result.state === 'in_progress') {
          return { ...w, revealed: result.revealed, worth: result.worth };
        }
        return w;
      });
      state.typed = [];
      state.caret = 0;
    },
    markSolved(state, { position, word, definition, points }) {
      const target = state.game.words.find((w) => w.position === position);
      if (target) {
        target.word = word;
        // O significado vem na mesma resposta: sem ele, a linha de definição sumia
        // depois da primeira palavra e só voltava se o jogador recarregasse a página.
        target.definition = definition ?? null;
        target.solved = true;
        target.points = points;
        target.revealed = null;
        target.worth = null;
      }
    },
    markFailed(state, { position, word, definition }) {
      const target = state.game.words.find((w) => w.position === position);
      if (target) {
        target.word = word;
        target.definition = definition ?? null;
        target.solved = false;
        target.revealed = null;
        target.worth = null;
      }
    },
    setMe(state, me) {
      state.me = me;
    },
    setTrail(state, trail) {
      state.trail = trail;
    },
    setTrailOutcome(state, outcome) {
      state.trailOutcome = outcome;
    },
    setDuels(state, duels) {
      state.duels = duels;
    },
    setDuelOutcome(state, outcome) {
      state.duelOutcome = outcome;
    },
    setSecondsLeft(state, seconds) {
      state.secondsLeft = seconds;
      // O limite da partida é o maior número que o servidor mandou nela — o primeiro. A
      // tela precisa dele para desenhar a barra e a largada, e não pode chutar 90: o
      // tempo é parâmetro do painel.
      if (state.duelLimit === null || seconds > state.duelLimit) state.duelLimit = seconds;
    },
    setCountdown(state, seconds) {
      state.countdown = seconds;
    },
    tickCountdown(state) {
      if (state.countdown > 0) state.countdown -= 1;
    },
    tickClock(state) {
      // Desconto local entre jogadas. Quem manda é o servidor: toda resposta reescreve
      // este número, e o zero aqui não encerra nada — quem encerra é ele.
      if (state.secondsLeft !== null && state.secondsLeft > 0) state.secondsLeft -= 1;
    },
  },

  actions: {
    async init({ commit, dispatch }) {
      commit('setDeviceId', await api.ensurePlayer());
      await Promise.all([dispatch('loadMe'), dispatch('loadModes')]);
      // Recarregar a página não pode zerar o dia: se a partida seguia aberta, ela volta;
      // se fechou, volta o resultado dela — e o placar do topo vem junto, nos dois casos.
      const retomou = await dispatch('resumeGame');
      // Só quem recarregou **na tela de jogo** volta vendo o fecho. Quem abriu o app em
      // outra aba e depois entrou em "Jogar" fez uma navegação, e ali a base é a escolha
      // de modo.
      const naTelaDeJogo = typeof window !== 'undefined' && window.location.pathname === '/';
      if (!retomou) {
        const releu = await dispatch('openResult');
        // Fora da tela de jogo, o fecho fica guardado sem abrir: quem chega depois pela
        // aba está escolhendo o que jogar, não relendo o que passou.
        commit('setResultToShow', releu && naTelaDeJogo);
      }
      commit('setReady', true);
    },

    async loadModes({ commit }) {
      try {
        commit('setModes', await api.fetchModes());
      } catch (error) {
        console.error('loadModes error:', error);
      }
    },

    async loadMe({ commit }) {
      try {
        commit('setMe', await api.fetchMe());
      } catch (error) {
        console.error('loadMe error:', error);
      }
    },

    async register({ dispatch }, payload) {
      const sessao = await api.register(payload);
      await dispatch('loadMe');
      return sessao;
    },

    async login({ dispatch }, payload) {
      const sessao = await api.login(payload);
      await dispatch('loadMe');
      return sessao;
    },

    async confirmPasswordReset({ dispatch }, payload) {
      const session = await api.confirmPasswordReset(payload);
      await dispatch('loadMe');
      return session;
    },

    async changePassword({ dispatch }, payload) {
      const session = await api.changePassword(payload);
      await dispatch('loadMe');
      return session;
    },

    async logout({ dispatch }) {
      api.logout();
      await dispatch('loadMe');
    },

    /**
     * Desiste da partida aberta, em qualquer modo.
     *
     * Quem encerra é o servidor: a resposta traz o estado final, e daqui só se busca o
     * fecho para a folha mostrar o que foi feito.
     */
    async forfeitGame({ state, commit, dispatch }) {
      if (!state.game || state.game.state !== 'in_progress') return;
      try {
        const fecho = await api.forfeitGame(state.game.game_id);
        commit('setGame', fecho.game);
        // O fecho vem na mesma resposta: a folha mostra o bônus do duelo e as estrelas da
        // fase mesmo quando a partida acabou por desistência ou por tempo.
        if (fecho.trail) commit('setTrailOutcome', fecho.trail);
        if (fecho.duel) commit('setDuelOutcome', fecho.duel);
        await dispatch('finish', null);
      } catch (error) {
        commit('setMessage', {
          tone: 'bad',
          icon: 'x',
          text: error.response?.data?.message || 'Não foi possível encerrar a partida.',
        });
      }
    },

    async loadDuels({ commit }) {
      try {
        commit('setDuels', await api.fetchDuels());
      } catch (error) {
        console.error('loadDuels error:', error);
      }
    },

    /**
     * Abre a minha vez do duelo e deixa a partida pronta na tela de jogo.
     *
     * O relógio vem junto: ele já está correndo no servidor desde esta chamada.
     */
    async playDuel({ commit }, id) {
      commit('setLoading', true);
      try {
        const { game, seconds_left: seconds, countdown } = await api.playDuel(id);
        commit('setCurrentModeKey', 'duel');
        commit('setGame', game);
        commit('setSecondsLeft', seconds);
        commit('setCountdown', countdown || 0);
        commit('setMessage', {
          tone: 'mute',
          icon: 'clock',
          text: `Duelo. ${seconds} segundos para a chain inteira.`,
        });
        return true;
      } catch (error) {
        const code = error.response?.data?.error;
        commit('setMessage', {
          tone: 'bad',
          icon: 'x',
          text: code === 'duel_unavailable'
            ? error.response?.data?.message || 'Este duelo não está disponível.'
            : 'Não foi possível abrir o duelo.',
        });
        return false;
      } finally {
        commit('setLoading', false);
      }
    },

    async loadTrail({ commit }) {
      try {
        commit('setTrail', await api.fetchTrail());
      } catch (error) {
        console.error('loadTrail error:', error);
      }
    },

    /**
     * Abre a fase e deixa a partida pronta na tela de jogo.
     *
     * Devolve `true` quando abriu: quem chamou decide para onde navegar, porque rota é
     * assunto da tela, não do store.
     */
    async startPhase({ commit, dispatch }, number) {
      commit('setLoading', true);
      try {
        const game = await api.startPhase(number);
        commit('setCurrentModeKey', 'trail');
        commit('setGame', game);
        commit('setTrailOutcome', null);
        commit('setMessage', {
          tone: 'mute',
          icon: 'link',
          text: `Fase ${number}. Qual palavra se liga a ${game.anchor.toUpperCase()}?`,
        });
        return true;
      } catch (error) {
        const code = error.response?.data?.error;
        commit('setMessage', {
          tone: code === 'out_of_lives' ? 'warn' : 'bad',
          icon: code === 'out_of_lives' ? 'clock' : 'x',
          text: {
            phase_locked: 'Essa fase ainda está travada.',
            out_of_lives: 'Sem energia agora. Espere a próxima vida.',
            no_chain_available: 'Essa fase ainda não tem chain cadastrada.',
          }[code] || 'Não foi possível abrir a fase.',
        });
        // A recusa muda a energia e a fase corrente; recarregar mostra o motivo na tela.
        await dispatch('loadTrail');
        return false;
      } finally {
        commit('setLoading', false);
      }
    },

    /**
     * Retoma a partida que ficou aberta neste navegador, se ainda estiver em andamento.
     *
     * Com `idPedido`, retoma aquela partida; sem ele, a que ficou guardada no navegador.
     * Devolve `true` quando ela ainda está em andamento. Quem chama decide o que fazer no
     * `false` — a tela de jogo abre o resultado, se a partida acabou, ou a folha de modos,
     * e nunca uma partida que o jogador não pediu.
     */
    /**
     * Retoma a partida **em andamento** guardada neste navegador.
     *
     * Devolve `true` só quando existe partida aberta, e nunca escreve result: quem lê
     * partida terminada é `openResult`. Eram a mesma ação antes, e por isso "ver
     * resultado" de um modo apagava a partida aberta de outro.
     */
    async resumeGame({ commit }, idPedido = null) {
      const id = idPedido || readStoredGame();
      if (!id) return false;
      try {
        const game = await api.resumeGame(id);
        if (game.state !== 'in_progress') return false;
        commit('setMode', game.mode);
        commit('setCurrentModeKey', modeOf(game));
        commit('setGame', game);

        // Partida de duelo tem relógio, e `setGame` zera o que a tela usa para desenhá-lo.
        // Sem reler o duelo, retomar mostrava 0:00 com a barra cheia — um cronômetro que
        // mente é pior que nenhum.
        if (game.duel_id) {
          const duel = await api.fetchDuel(game.duel_id).catch(() => null);
          if (duel) {
            commit('setSecondsLeft', duel.seconds_left ?? duel.time_limit_seconds);
            commit('setCountdown', duel.countdown || 0);
          }
        }

        commit('setMessage', {
          tone: 'mute',
          icon: game.duel_id ? 'clock' : 'link',
          text: `Qual palavra se liga a ${game.anchor.toUpperCase()}?`,
        });
        return true;
      } catch {
        forgetStoredGame();
        return false;
      }
    },

    /**
     * Abre o resultado de uma partida **terminada**, em leitura.
     *
     * Não encosta em `state.game`: com partida aberta, o resultado é uma folha por cima, e
     * o jogador volta para ela quando fecha. Sem id, usa a última partida deste navegador.
     */
    async openResult({ commit }, idPedido = null) {
      const id = idPedido || readStoredGame();
      if (!id) return false;
      try {
        const game = await api.resumeGame(id);
        if (game.state === 'in_progress') return false;
        commit('setCurrentModeKey', modeOf(game));
        commit('setResult', { game, summary: await api.getSummary(game.game_id).catch(() => null) });
        // Pedido explícito abre a folha; quem decide o quando é a tela, ao aplicar a base.
        commit('setResultToShow', true);
        return true;
      } catch {
        return false;
      }
    },

    /**
     * Ponto único de troca de partida.
     *
     * Toda porta que abriria outra partida — modo, fase da trilha, duelo — passa por aqui.
     * Com uma partida aberta, a troca vira pedido de confirmação e o casco do app pergunta;
     * sem partida, executa direto. Antes o aviso morava dentro da folha de modos da tela de
     * jogo, e por isso o cartão de Hoje trocava a partida por baixo dele.
     */
    async switchGame({ state, getters, commit, dispatch }, plan) {
      // Pedir o que já está aberto é **retomar**, não switchGame: a fase da partida ativa, o
      // duelo dela ou o mesmo modo não podem cair no aviso de desistência.
      if (state.game && isSameGame(state.game, plan)) return true;
      // Só partida em andamento vira pergunta: desistir do que já terminou não existe.
      if (getters.hasGame) {
        commit('setPendingSwitch', plan);
        return false;
      }
      return dispatch('runSwitch', plan);
    },

    /** Confirmou: a partida aberta é encerrada de verdade, e só então a outra abre. */
    async confirmSwitch({ state, commit, dispatch }) {
      const plan = state.pendingSwitch;
      commit('setPendingSwitch', null);
      if (!plan) return false;
      await dispatch('forfeitGame');
      return dispatch('runSwitch', plan);
    },

    cancelSwitch({ commit }) {
      commit('setPendingSwitch', null);
    },

    runSwitch({ dispatch }, plan) {
      if (plan.kind === 'phase') return dispatch('startPhase', plan.value);
      if (plan.kind === 'duel') return dispatch('playDuel', plan.value);
      return dispatch('startGame', plan.value || null);
    },

    /** Sair da tela com duelo aberto é desistir dele; o casco pergunta antes de navegar. */
    requestExit({ commit }, rota) {
      commit('setPendingExit', rota);
    },

    async confirmExit({ state, commit, dispatch }) {
      const rota = state.pendingExit;
      commit('setPendingExit', null);
      await dispatch('forfeitGame');
      // Quem saiu não pediu para ver o fecho: ele fica guardado, e a tela de jogo volta a
      // ser a escolha de modo. A folha tem a entrada para relê-lo.
      commit('setResultToShow', false);
      return rota;
    },

    cancelExit({ commit }) {
      commit('setPendingExit', null);
    },

    async startGame({ commit, dispatch, state, getters }, mode = null) {
      // A tela pode abrir antes de `init` terminar. Sem esperar a lista, o jogo caía no
      // "nenhum modo disponível" logo no primeiro carregamento.
      if (!mode && !state.modes.length) await dispatch('loadModes');

      // Sem modo explícito, vale o primeiro que o servidor ofereceu — ele já filtrou o
      // que tem conteúdo. Uma chave fixa aqui quebra a tela assim que o painel desativa
      // aquele modo.
      const chosen = mode || getters.dailyMode?.key || state.modes[0]?.key;
      if (!chosen) {
        commit('setMessage', {
          tone: 'bad',
          icon: 'x',
          text: 'Nenhum modo disponível agora. Volte mais tarde.',
        });
        return;
      }
      const anterior = state.mode;
      commit('setMode', chosen);
      commit('setCurrentModeKey', chosen);
      commit('setLoading', true);
      try {
        const game = await api.startGame(chosen);
        commit('setGame', game);

        if (game.state !== 'in_progress') {
          // A diária de hoje já foi jogada. Em vez de recusar, o servidor devolve a
          // partida encerrada e o jogador revê o que fez.
          commit('setSummary', await api.getSummary(game.game_id));
          commit('setMessage', { tone: 'mute', icon: 'check', text: 'Você já jogou a chain de hoje.' });
          return;
        }

        commit('setMessage', {
          tone: 'mute',
          icon: 'link',
          text: `Qual palavra se liga a ${game.anchor.toUpperCase()}?`,
        });
      } catch (error) {
        const code = error.response?.data?.error;
        // O modo volta a ser o de antes: deixar o seletor marcando um modo que não abriu
        // faz a tela parecer travada.
        commit('setMode', anterior);
        commit('setMessage', {
          tone: 'bad',
          icon: 'x',
          text: {
            no_chain_available: 'Esse modo ficou sem chain. Escolha outro.',
            mode_not_found: 'Esse modo de jogo não está disponível.',
          }[code] || 'Não foi possível iniciar o jogo.',
        });
      } finally {
        commit('setLoading', false);
      }
    },

    async submitGuess({ state, commit, getters, dispatch }) {
      if (!getters.canSubmit) return;

      const word = getters.currentWord;
      const palpite = (word.revealed || '') + state.typed.map((letter) => letter || '').join('');
      const position = word.position;
      const valorAntes = word.worth;

      commit('setLocked', true);
      let result;
      try {
        result = await api.submitGuess(state.game.game_id, palpite);
      } catch (error) {
        const code = error.response?.data?.error;
        // Recusa não é erro do jogador: o servidor não cobrou letra nem ponto, e o que
        // ele digitou continua na tela para ser corrigido. Por isso ela não usa o
        // vermelho de palpite errado — dizer "você errou" quando nada foi cobrado é
        // mentira cara: o jogador passa a evitar tentar.
        const RECUSAS = {
          not_a_word: {
            text: error.response?.data?.message || 'Essa palavra eu não conheço.',
            icon: 'book',
          },
          repeated_guess: {
            text: 'Você já tentou essa palavra. Nada foi cobrado — tente outra.',
            icon: 'refresh',
          },
        };
        // Partida já encerrada do outro lado — tempo do duelo, prazo, outra aba. A tela
        // estava fora de sincronia, e insistir no palpite não é o caminho: ela recarrega
        // o estado e mostra o fim.
        if (code === 'game_already_finished') {
          commit('setMessage', { tone: 'mute', icon: 'clock', text: 'Esta partida já foi encerrada.' });
          commit('setLocked', false);
          await dispatch('reloadGame');
          return undefined;
        }

        const recusa = RECUSAS[code];
        commit('setMessage', {
          tone: recusa ? 'warn' : 'bad',
          icon: recusa?.icon || 'x',
          text: recusa?.text
            || (code === 'too_many_guesses' ? 'Devagar. Espere alguns segundos.' : 'Falha ao enviar o palpite.'),
        });
        commit('setLocked', false);
        // Quem chamou usa isto para dar o aviso na palavra, e não o tremor de erro.
        return recusa ? { rejected: code } : undefined;
      }

      state.lastPosition = position;
      commit('setOutcome', { ...result, position });
      // Retorno por casa: vem vazio nos modos ranqueados, e aí a tela não desenha banda.
      commit('setFeedback', { letters: result.letters, keyboard: result.keyboard });
      // Na trilha, o fecho da partida traz estrelas, XP e energia na mesma resposta.
      if (result.trail) commit('setTrailOutcome', result.trail);
      // No duelo, toda resposta traz o relógio do servidor — o daqui é só desenho.
      if (result.seconds_left !== null && result.seconds_left !== undefined) {
        commit('setSecondsLeft', result.seconds_left);
      }
      if (result.duel) commit('setDuelOutcome', result.duel);

      if (result.correct) {
        commit('markSolved', {
          position,
          word: result.word,
          definition: result.word_definition,
          points: result.word_points,
        });
      } else if (result.state !== 'in_progress') {
        commit('markFailed', { position, word: result.word, definition: result.word_definition });
      }

      commit('applyGuess', result);

      if (result.state === 'in_progress') {
        commit('setMessage',
          result.correct
            ? { tone: 'good', icon: 'check', text: 'Certo. Próxima da chain.' }
            : {
              tone: 'warn',
              icon: 'flame',
              // Sem valor novo a frase não se completa: a partida acabou junto com o
              // palpite, e "agora vale null" foi o que apareceu na tela.
              text: result.worth === null || result.worth === undefined
                ? 'Não. Mais uma letra revelada.'
                : `Não. Mais uma letra — agora vale ${result.worth}.`,
            });
      } else {
        await dispatch('finish', result);
      }
      commit('setLocked', false);
      return { correct: result.correct, valorAntes };
    },

    /**
     * Fecha a partida de duelo quando o relógio zera.
     *
     * O servidor só percebia o tempo esgotado no palpite seguinte, então quem parava de
     * digitar ficava numa tela em 0:00 que ainda aceitava letra. O fecho é o mesmo da
     * expiração — encerra com o que foi feito, e o que faltava vale zero.
     */
    async settleDuelTime({ state, commit, dispatch, getters }) {
      if (!state.game || !getters.gameDuel || getters.isOver) return;
      try {
        const fecho = await api.forfeitGame(state.game.game_id);
        commit('setGame', fecho.game);
        if (fecho.duel) commit('setDuelOutcome', fecho.duel);
      } catch {
        // Já encerrada do outro lado: o estado verdadeiro vem da releitura abaixo.
        await dispatch('reloadGame');
        return;
      }
      commit('setSecondsLeft', 0);
      await dispatch('finish', state.game);
    },

    /**
     * Relê o duelo enquanto a folha de fim diz "esperar quem falta jogar".
     *
     * O fecho do duelo acontece por ação dos outros, ou pelo relógio deles zerando, e nada
     * disso chega sozinho à folha: ela contava a verdade do instante em que abriu.
     */
    async refreshDuelOutcome({ state, commit }) {
      const outcome = state.duelOutcome;
      if (!outcome || outcome.closed) return;
      const duel = await api.fetchDuel(outcome.duel_id);
      // A folha pode ter mudado de duelo enquanto a leitura voava.
      if (duel.status === 'closed' && state.duelOutcome?.duel_id === outcome.duel_id) {
        commit('setDuelOutcome', { ...state.duelOutcome, closed: true });
      }
    },

    /** Rebusca a partida no servidor. Ele é dono do estado; a tela só o reflete. */
    async reloadGame({ state, commit, dispatch }) {
      if (!state.game) return;
      try {
        const game = await api.fetchGame(state.game.game_id);
        commit('setGame', game);
        if (game.state !== 'in_progress') await dispatch('finish', game);
      } catch {
        // Partida que sumiu do servidor não tem o que refletir; a tela fica como está.
      }
    },

    /**
     * A partida acabou: ela sai do slot de quem joga e entra no de leitura.
     *
     * É o que garante a regra da tela: com `game` valendo só para partida em andamento, o
     * estado base de "/" volta a ser a folha de modos assim que a chain fecha, e o fecho
     * aparece por cima, como folha.
     */
    async finish({ state, commit, getters, dispatch }) {
      commit('setMessage', null);
      const closed = state.game;
      let fecho = null;
      try {
        fecho = await api.getSummary(closed.game_id);
      } catch {
        fecho = null;
      }
      commit('setSummary', fecho);
      // Estatística, XP e conquistas foram atualizados no servidor no fecho da partida.
      // A fase da trilha também mexe no XP e no mapa, mesmo sem pontuar ranking.
      const eraTrilha = getters.gamePhase;
      const eraDuelo = getters.gameDuel;
      const pontuava = getters.isScored;

      commit('setGame', null);
      commit('setResult', { game: closed, summary: fecho });
      // Acabou de jogar: o fecho abre. Quem controla o overlay é este flag, e só ele.
      commit('setResultToShow', true);

      if (pontuava || eraTrilha) await dispatch('loadMe');
      if (eraTrilha) await dispatch('loadTrail');
      if (eraDuelo) await dispatch('loadDuels');
    },

    press({ commit, getters, dispatch }, key) {
      if (key === 'ENTER') return dispatch('submitGuess');
      if (key === 'BACK') return commit('deleteLetter');
      if (/^[A-Z]$/.test(key) && !getters.isOver) return commit('typeLetter', fold(key));
      return undefined;
    },

    async deleteAccount({ dispatch }) {
      await api.deleteAccount();
      await dispatch('loadMe');
    },
  },
});
